import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import translations from './AlgorithmTargetFormModal.translations.json';
import { AlgorithmTargetRuleType } from '../../types';
import { createDefaultTargetRuleFormData } from './schema/defaultValues';
import { Flex } from 'antd';
import { DividerHeader } from '../../../common/components/dividerHeader/DividerHeader';
import { AlgorithmTargetRuleTypeField } from './AlgorithmTargetRuleTypeField';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmTargetRuleFormDataType } from './schema/types';
import { getMaxRuleCode } from './utils';
import { AlgorithmType } from '../schema/zAlgorithmType';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-modal',
);
addTranslations(translations);

export interface AlgorithmTargetRuleTypeFieldsProps {
  algorithmTypeCode: AlgorithmType;
  maxRuleCode: number;
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
}

export function AlgorithmTargetRuleTypeFields({
  algorithmTypeCode,
  maxRuleCode,
  sources,
  mappings,
}: AlgorithmTargetRuleTypeFieldsProps) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const rules: AlgorithmTargetRuleFormDataType[] = useWatch({ name: 'rules' });

  const onAdd = useCallback(() => {
    const maxCode = getMaxRuleCode(rules);

    setValue(
      'rules',
      rules.concat(
        createDefaultTargetRuleFormData(
          AlgorithmTargetRuleType.RULE,
          String(Math.max(maxCode, maxRuleCode) + 1),
        ),
      ),
      { shouldDirty: true },
    );
  }, [rules, setValue, maxRuleCode]);

  const onRemove = useCallback(
    (index: number) => {
      setValue('rules', rules.toSpliced(index, 1), { shouldDirty: true });
    },
    [rules, setValue],
  );

  const ruleIndexes = useMemo(() => {
    const indexes: number[] = [];
    (rules || []).forEach((rule, index) => {
      if (rule.ruleType === AlgorithmTargetRuleType.RULE) {
        indexes.push(index);
      }
    });
    return indexes;
  }, [rules]);

  const sectionBottomRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(ruleIndexes.length);

  useEffect(() => {
    const prev = prevCountRef.current;

    if (ruleIndexes.length > prev && sectionBottomRef.current) {
      sectionBottomRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }

    prevCountRef.current = ruleIndexes.length;
  }, [ruleIndexes.length]);

  return (
    <DividerHeader
      id="algorithm-target-rule"
      title={t('ruleTypeRules.label')}
      count={ruleIndexes?.length}
      onAddButtonClick={onAdd}
    >
      <Flex vertical gap="middle">
        {(ruleIndexes || []).map((ruleIndex, index) => (
          <AlgorithmTargetRuleTypeField
            key={ruleIndex}
            algorithmTypeCode={algorithmTypeCode}
            index={ruleIndex}
            isLast={index >= ruleIndexes?.length - 1}
            onAdd={onAdd}
            onRemove={onRemove}
            sources={sources}
            mappings={mappings}
          />
        ))}

        <div ref={sectionBottomRef} />
      </Flex>
    </DividerHeader>
  );
}
